import { FC, ReactNode, useContext, useRef, useState } from "react";
import { useRouter } from "next/router";
import useUser from "@src/features/app/hooks/useUser";
import {
  resolveFeatureFlag,
  useFeatureFlagsLoaded,
} from "@src/features/app/hooks/useFeatureFlag";
import { FeatureFlagContext } from "@src/lib/contexts/FeatureFlagContext";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import { Button } from "@src/lib/ui/Button";
import { UserType } from "@src/types.generated";
import ReleaseEnvelope from "./ReleaseEnvelope";
import FirstAgentBanner from "./FirstAgentBanner";
import ReleaseHeadlineCard from "./ReleaseHeadlineCard";
import {
  getActiveRelease,
  ReleaseExperience,
  ReleaseHeadlineRelease,
} from "./releases";

// `?release_preview=<slug>` shows a release outside its window, ignores
// dismissal, and replays the celebration on every load so copy can be QA'd.
// Add `&style=card|envelope` to preview a specific experience.
const PREVIEW_QUERY_PARAM = "release_preview";
const STYLE_QUERY_PARAM = "style";
const EXPERIENCES: ReleaseExperience[] = ["card", "envelope"];
// Admins only for now; add UserType.Manager to show Managers their version
// (their subline and tiles are already in `releases.ts`).
const AUDIENCE = [UserType.Admin];

const ReleaseHeadline: FC = () => {
  const { user } = useUser();
  const { query, isReady } = useRouter();
  const previewParam = query[PREVIEW_QUERY_PARAM];
  const previewSlug =
    typeof previewParam === "string" ? previewParam : undefined;
  const release = getActiveRelease(new Date(), previewSlug);
  const flags = useContext(FeatureFlagContext);
  const flagOn = release?.flag ? resolveFeatureFlag(flags, release.flag) : true;
  const flagsLoaded = useFeatureFlagsLoaded();
  // Flags can refetch (e.g. when the org changes), briefly reading as unloaded
  // and off; keep the first answer so the release doesn't vanish mid-open.
  const flagChoice = useRef<boolean | null>(null);
  // Previews get a Replay button that remounts the release from the start.
  const [replays, setReplays] = useState(0);
  if (flagChoice.current === null && flagsLoaded) {
    flagChoice.current = flagOn;
  }

  // The homepage is statically optimized, so the query is empty until the
  // router is ready; waiting avoids mounting the wrong (non-preview) variant.
  if (!isReady || !user || !AUDIENCE.includes(user.userType) || !release) {
    return null;
  }
  const isPreview = Boolean(previewSlug);
  // A flagged release waits for flags, then shows only where the flag is on.
  if (release.flag && !isPreview && !flagChoice.current) {
    return null;
  }

  const styleParam = query[STYLE_QUERY_PARAM];
  const previewExperience = EXPERIENCES.find(
    (experience) => previewSlug && experience === styleParam,
  );
  const experience = previewExperience ?? release.experience;

  const key = `${release.slug}-${replays}`;
  const content =
    experience === "envelope" ? (
      <ReleaseEnvelopeExperience
        key={key}
        release={release}
        isPreview={isPreview}
      />
    ) : (
      <ReleaseHeadlineCard
        key={key}
        release={release}
        isPreview={isPreview}
        celebrate
      />
    );
  if (!isPreview) return content;
  return (
    <PreviewReplay onReplay={() => setReplays((count) => count + 1)}>
      {content}
    </PreviewReplay>
  );
};

const PreviewReplay: FC<{ onReplay: () => void; children: ReactNode }> = ({
  onReplay,
  children,
}) => (
  <>
    {children}
    <Button
      variant="outline"
      size="sm"
      className="fixed right-4 bottom-4 z-50 shadow-md"
      onClick={onReplay}
    >
      Replay release preview
    </Button>
  </>
);

// First visit: the envelope pops up and opens by itself. Once it has been
// opened, the homepage shows only a small "add your first agent" banner.
export const ReleaseEnvelopeExperience: FC<{
  release: ReleaseHeadlineRelease;
  isPreview: boolean;
}> = ({ release, isPreview }) => {
  const [storedOpened, setStoredOpened] = useBooleanLocalStorage(
    `opened_release_envelope_${release.slug}`,
    false,
  );
  // Opening the envelope is the celebration, so the card won't replay confetti
  // even if the release later switches to the card experience. Likewise, a
  // viewer who already saw the card (before the envelope flag was turned on)
  // goes straight to the banner instead of getting the envelope.
  const [celebrated, setCelebrated] = useBooleanLocalStorage(
    `celebrated_release_headline_${release.slug}`,
    false,
  );
  // Previews replay the envelope on every load without touching storage.
  const [previewOpened, setPreviewOpened] = useState(false);
  const opened = isPreview ? previewOpened : storedOpened || celebrated;
  const [showingLetter, setShowingLetter] = useState(false);
  const [closedLetter, setClosedLetter] = useState(false);

  if (!opened || showingLetter) {
    return (
      <ReleaseEnvelope
        release={release}
        onOpen={() => {
          setShowingLetter(true);
          if (isPreview) setPreviewOpened(true);
          else {
            setStoredOpened(true);
            setCelebrated(true);
          }
        }}
        onClose={() => {
          setShowingLetter(false);
          setClosedLetter(true);
        }}
      />
    );
  }
  // The letter already made the announcement; the homepage keeps only a
  // small nudge to add a first agent.
  return <FirstAgentBanner isPreview={isPreview} focusOnMount={closedLetter} />;
};

export default ReleaseHeadline;
